import React, { useState, useEffect, useCallback, useRef } from 'react';
import TWPPriceCalculator from '../../components/TWPPriceCalculator';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { generateQuotePDFBlob } from '../../services/estimate-pdf';
import PDFPreviewModal from '../../components/PDFPreviewModal';
import {
  createEstimate, updateEstimate, getEstimate,
  LINE_TYPES, WINDOW_TYPES, DOOR_TYPES, ADDER_TYPES, ORDERED_WINDOW_TYPES_LIST,
  EMPTY_LINE, TWP_WINDOW_SUBTYPES, computeQboName,
  ORDERED_PRODUCT_OPTIONS, detectOrderedProductFromDescription,
  DOOR_PRODUCT_OPTIONS, detectDoorProductFromDescription
} from '../../services/estimate-service';
import { useAuth } from '../../contexts/AuthContext';
import { getAssignableUsers } from '../../services/user-service';
import { useConfirm } from '../../components/ConfirmDialog';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import {
  ArrowLeft, CheckCircle2, Send, Mail, Smartphone, AlertTriangle, ClipboardCheck,
} from 'lucide-react';
import { noAutofill, noAutofillContainer } from '../../utils/noAutofill';

// ── Palette — same warm, coherent family as the rest of Estimates.
const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const SUBTYPE_MAP = {
  'Double Hung':   'dh',
  'Slider':        'hs',
  '3 Lite Slider': 'threeLiteHS',
  'Picture Window':'pw',
  'Casement':      'csmt',
  'Awning':        'csmt',
};
const SUBTYPE_TO_TYPE = {
  'dh':          'Double Hung',
  'hs':          'Slider',
  'threeLiteHS': '3 Lite Slider',
  'pw':          'Picture Window',
  'csmt':        'Casement',
};

// ── Tooltip input ─────────────────────────────────────────────────────────────
function TooltipInput({ value, onChange, placeholder, style, ...rest }) {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos]         = useState({ top: 0, left: 0, width: 0 });
  const ref = useRef(null);
  const show = hovered && value && value.length > 18;
  const onEnter = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.top - 8, left: r.left, width: Math.max(r.width, 300) });
    }
    setHovered(true);
  };
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input ref={ref} value={value || ''} onChange={onChange} placeholder={placeholder}
        style={style} onMouseEnter={onEnter} onMouseLeave={() => setHovered(false)} {...rest} />
      {show && (
        <div style={{
          position: 'fixed', top: pos.top, left: pos.left,
          transform: 'translateY(-100%)', zIndex: 99999,
          background: '#1e293b', color: 'white', padding: '8px 13px',
          borderRadius: '9px', fontSize: '13px', fontFamily: "'Segoe UI', system-ui, sans-serif",
          maxWidth: Math.max(pos.width, 380), minWidth: 200,
          boxShadow: '0 8px 28px rgba(0,0,0,0.28)', pointerEvents: 'none',
          lineHeight: 1.55, wordBreak: 'break-word', marginBottom: '5px',
        }}>
          {value}
          <div style={{ position: 'absolute', bottom: -6, left: 18, width: 0, height: 0,
            borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
            borderTop: '6px solid #1e293b' }} />
        </div>
      )}
    </div>
  );
}

// ── Combobox ──────────────────────────────────────────────────────────────────
function TooltipCombobox({ value, onChange, suggestions, placeholder }) {
  const [open, setOpen]     = useState(false);
  const [hov, setHov]       = useState(false);
  const [tPos, setTPos]     = useState({ top: 0, left: 0, width: 0 });
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);
  const filtered = (suggestions || []).filter(s => s.toLowerCase().includes((value || '').toLowerCase()));
  const show = hov && value && value.length > 18;
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <input value={value || ''} onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => {
          if (ref.current) { const r = ref.current.getBoundingClientRect(); setOpenUp(window.innerHeight - r.bottom < 200); }
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onMouseEnter={() => {
          if (ref.current) { const r = ref.current.getBoundingClientRect(); setTPos({ top: r.top - 8, left: r.left, width: Math.max(r.width, 300) }); }
          setHov(true);
        }}
        onMouseLeave={() => setHov(false)}
        placeholder={placeholder}
        style={{ width: '100%', padding: '7px 9px', border: '1px solid #e8e0d8',
          borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit',
          boxSizing: 'border-box', outline: 'none', height: '36px', background: 'white' }} />
      {show && (
        <div style={{ position: 'fixed', top: tPos.top, left: tPos.left, transform: 'translateY(-100%)',
          zIndex: 99999, background: '#1e293b', color: 'white', padding: '8px 13px',
          borderRadius: '9px', fontSize: '13px', maxWidth: Math.max(tPos.width, 380), minWidth: 200,
          boxShadow: '0 8px 28px rgba(0,0,0,0.28)', pointerEvents: 'none',
          lineHeight: 1.55, wordBreak: 'break-word', marginBottom: '5px' }}>
          {value}
          <div style={{ position: 'absolute', bottom: -6, left: 18, width: 0, height: 0,
            borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
            borderTop: '6px solid #1e293b' }} />
        </div>
      )}
      {open && filtered.length > 0 && (
        <div style={{
          position: 'fixed',
          top: ref.current ? (openUp
            ? ref.current.getBoundingClientRect().top - Math.min(filtered.length * 36, 180)
            : ref.current.getBoundingClientRect().bottom + 2) : 0,
          left: ref.current ? ref.current.getBoundingClientRect().left : 0,
          width: Math.max(ref.current ? ref.current.getBoundingClientRect().width : 0, 200),
          background: 'white', border: '1px solid #e5e5e5', borderRadius: '9px',
          boxShadow: '0 6px 24px rgba(0,0,0,0.13)', zIndex: 99999,
          maxHeight: '180px', overflowY: 'auto',
        }}>
          {filtered.map((s, i) => (
            <div key={s || i} onMouseDown={() => { onChange(s); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: '#2b2b2b', borderBottom: '1px solid #f5f5f5' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fdf6f0'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Type badge ────────────────────────────────────────────────────────────────
function TypeBadge({ type, onClick }) {
  const t = LINE_TYPES.find(l => l.value === type) || LINE_TYPES[0];
  return (
    <div onClick={onClick} title="Click to change type"
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '4px 8px', background: t.bg, color: t.color, borderRadius: '5px',
        fontSize: '10px', fontWeight: '600', cursor: 'pointer',
        whiteSpace: 'nowrap', userSelect: 'none',
        width: '100%', boxSizing: 'border-box', letterSpacing: '0.2px',
        transition: 'opacity 0.15s' }}>
      {t.label}
    </div>
  );
}

// ── Edit config button ────────────────────────────────────────────────────────
function EditConfigBtn({ onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      title="Re-open in configurator to edit"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 9px', borderRadius: '5px', cursor: 'pointer',
        fontFamily: 'inherit', fontSize: '11px', fontWeight: '500',
        border: '0.5px solid #d4d0c8',
        background: hov ? '#f5f3f0' : 'white',
        color: '#555', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
      Edit
    </button>
  );
}

// ── Customer field label ──────────────────────────────────────────────────────
function F({ label, children, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', ...style }}>
      <label style={{ fontSize: '10px', fontWeight: '600', color: '#999',
        textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Shared input style ────────────────────────────────────────────────────────
const INP = {
  padding: '7px 10px', border: '0.5px solid #d4d0c8', borderRadius: '7px',
  fontSize: '13px', fontFamily: 'inherit', outline: 'none',
  width: '100%', boxSizing: 'border-box', height: '36px', background: 'white',
  color: '#1a1a1a', transition: 'border-color 0.15s, box-shadow 0.15s',
};

// ── Column grid ───────────────────────────────────────────────────────────────
// drag(20) | type(88) | qty(58) | description(1.6fr) | room(130) | W"(54) | H"(54) | D"(54) | UI/Notes(1fr) | unit$(88) | total$(96) | actions(50)
// ALL 12 columns present for window/door/mulled rows
// Adder rows: description spans room+W+H+D (4 cols), then UI, unit, total, actions
const ROW_GRID = '20px 88px 58px 1.6fr 130px 54px 54px 54px 1fr 88px 96px 50px';

// ── Row action buttons ────────────────────────────────────────────────────────
function RowActions({ onDuplicate, onRemove }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1px', justifyContent: 'flex-end' }}>
      <button onClick={onDuplicate} title="Duplicate"
        style={{ background: 'none', border: 'none', color: '#d0c8c0', cursor: 'pointer',
          fontSize: '14px', padding: '3px 4px', lineHeight: 1, borderRadius: '4px' }}
        onMouseEnter={e => e.currentTarget.style.color = WALNUT}
        onMouseLeave={e => e.currentTarget.style.color = '#d0c8c0'}>⧉</button>
      <button onClick={onRemove} title="Remove"
        style={{ background: 'none', border: 'none', color: '#d0c8c0', cursor: 'pointer',
          fontSize: '17px', padding: '2px 4px', lineHeight: 1, borderRadius: '4px' }}
        onMouseEnter={e => e.currentTarget.style.color = RUST}
        onMouseLeave={e => e.currentTarget.style.color = '#d0c8c0'}>×</button>
    </div>
  );
}

// ── Drag handle ───────────────────────────────────────────────────────────────
// This is the ONLY draggable element on a row — the rest of the row stays free
// for clicks, double-clicks, and text selection. Pass `onDragStart` and
// `onDragEnd` from the parent to wire reordering.
function DragHandle({ onDragStart, onDragEnd }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{ cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#ccc', fontSize: '13px', userSelect: 'none', touchAction: 'none', padding: '2px',
        borderRadius: '4px' }}
      title="Drag to reorder"
      onMouseEnter={e => e.currentTarget.style.color = WALNUT}
      onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
      onMouseDown={e => e.currentTarget.style.cursor = 'grabbing'}
      onMouseUp={e => e.currentTarget.style.cursor = 'grab'}>
      ⠿
    </div>
  );
}

// ── Small dimension input ─────────────────────────────────────────────────────
function DimInput({ value, onChange, placeholder }) {
  return (
    <input value={value || ''} onChange={onChange} placeholder={placeholder}
      style={{ ...INP, textAlign: 'center', fontSize: '12px', padding: '7px 4px',
        fontVariantNumeric: 'tabular-nums' }} />
  );
}

// ── Money input ───────────────────────────────────────────────────────────────
function MoneyInput({ value, onChange, placeholder, highlight }) {
  return (
    <input type="number" value={value || ''} min="0" step="0.01" placeholder={placeholder}
      onChange={onChange}
      style={{ ...INP, textAlign: 'right', fontWeight: '500',
        fontVariantNumeric: 'tabular-nums',
        color: highlight ? '#1a1a1a' : '#aaa',
        background: highlight ? 'white' : '#fafafa' }} />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LINE ROW — all 12 columns every time
// ═══════════════════════════════════════════════════════════════════════════════
function LineRow({ line, onChange, onRemove, onDuplicate, onEditConfig, onDragStart, onDragEnd }) {
  const isNote   = line.type === 'note';
  const isAdder  = line.type === 'adder';
  const isWindow  = line.type === 'twp_window';
  const isOrdered = line.type === 'ordered_window';
  const isDoor    = line.type === 'door';
  const isMulled = line.isMulledUnit;

  // Subtle bg per row type. No zebra striping — cleaner look.
  const rowBg = isMulled ? '#faf7ff'
              : isAdder  ? '#f9fbf6'
              : 'white';

  const cycleType = () => {
    const types = LINE_TYPES.map(l => l.value);
    onChange(line.id, 'type', types[(types.indexOf(line.type) + 1) % types.length]);
  };

  const getSuggestions = () => {
    if (line.type === 'door')   return DOOR_TYPES;
    if (line.type === 'adder')  return ADDER_TYPES;
    if (line.type === 'ordered_window') return ORDERED_WINDOW_TYPES_LIST;
    if (line.type === 'twp_window') return WINDOW_TYPES;
    return [];
  };

  // qty: update total when qty changes
  const onQtyChange = v => {
    const qty = v, unit = parseFloat(line.price) || 0;
    const total = unit && v ? (unit * (parseFloat(v) || 1)).toFixed(2) : line.totalPrice || '';
    onChange(line.id, { qty, totalPrice: total });
  };
  // unit price: recalc total
  const onUnitChange = v => {
    const qty = parseFloat(line.qty) || 1;
    const total = v !== '' ? (parseFloat(v) * qty).toFixed(2) : '';
    onChange(line.id, { price: v, totalPrice: total });
  };
  // total: back-calc unit price
  const onTotalChange = v => {
    const qty = parseFloat(line.qty) || 1;
    const unit = v !== '' ? (parseFloat(v) / qty).toFixed(2) : '';
    onChange(line.id, { price: unit, totalPrice: v });
  };

  const computedTotal = line.price && line.qty
    ? (parseFloat(line.price) * parseFloat(line.qty)).toFixed(2)
    : '';
  const displayTotal = line.totalPrice !== undefined ? line.totalPrice : computedTotal;

  // ── NOTE row ─────────────────────────────────────────────────────────────
  if (isNote) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
        padding: '8px 10px', background: '#fffaf0',
        borderBottom: '0.5px solid #f0ebe4', borderLeft: '3px solid #f59e0b' }}>
        <DragHandle onDragStart={onDragStart} onDragEnd={onDragEnd} />
        <div style={{ width: 70, flexShrink: 0 }}>
          <TypeBadge type={line.type} onClick={cycleType} />
        </div>
        <div style={{ flex: 1, marginLeft: 6, minWidth: 0 }}>
          <TooltipInput value={line.description || ''} onChange={e => onChange(line.id, 'description', e.target.value)}
            placeholder="Type your note here — it will appear between the windows above and below..."
            style={{ ...INP, fontStyle: 'italic', color: '#78716c',
              background: 'white', border: '1px solid #f59e0b55', borderRadius: '6px',
              padding: '6px 10px', fontSize: '12px', width: '100%', boxSizing: 'border-box' }} />
        </div>
        <RowActions onDuplicate={() => onDuplicate(line.id)} onRemove={() => onRemove(line.id)} />
      </div>
    );
  }

  // ── MULLED row ────────────────────────────────────────────────────────────
  if (isMulled) {
    return (
      <div style={{ background: rowBg, borderBottom: '0.5px solid #ede8f5', borderLeft: '3px solid #7c3aed' }}>

        <div style={{ display: 'grid', gridTemplateColumns: ROW_GRID, gap: '5px',
          alignItems: 'center', padding: '7px 10px' }}>
          <DragHandle onDragStart={onDragStart} onDragEnd={onDragEnd} />
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '3px 8px', background: '#ede0fa', color: '#5a2a7a',
            borderRadius: '5px', fontSize: '10px', fontWeight: '600',
            letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>Mulled</div>
          {/* Qty */}
          <input type="number" value={line.qty || 1} min="1" onChange={e => onQtyChange(e.target.value)}
            style={{ ...INP, textAlign: 'center' }} />
          {/* Description */}
          <TooltipInput value={line.description || ''} onChange={e => onChange(line.id, 'description', e.target.value)}
            style={{ ...INP, fontWeight: '600', color: '#5a2a7a',
              background: '#faf7ff' }} />
          {/* Room */}
          <TooltipInput value={line.room || ''} onChange={e => onChange(line.id, 'room', e.target.value)}
            placeholder="Room" style={INP} />
          {/* W" — shows overall width (read-only display) */}
          <div style={{ fontSize: '12px', color: '#5a2a7a', fontWeight: '600', textAlign: 'center',
            padding: '6px 3px', background: '#ede0fa', borderRadius: '6px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {line.overallWidth ? `${line.overallWidth}"` : '—'}
          </div>
          {/* H" */}
          <div style={{ fontSize: '12px', color: '#5a2a7a', fontWeight: '600', textAlign: 'center',
            padding: '6px 3px', background: '#ede0fa', borderRadius: '6px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {line.overallHeight ? `${line.overallHeight}"` : '—'}
          </div>
          {/* D" */}
          <DimInput value={line.depth} onChange={e => onChange(line.id, 'depth', e.target.value)} placeholder='D"' />
          {/* UI / Notes */}
          <TooltipInput value={line.ui || ''} onChange={e => onChange(line.id, 'ui', e.target.value)}
            placeholder="Notes / UI" style={INP} />
          {/* Unit price */}
          <MoneyInput value={line.price} onChange={e => onUnitChange(e.target.value)}
            placeholder="$/unit" highlight={!!line.price} />
          {/* Total */}
          <MoneyInput value={displayTotal} onChange={e => onTotalChange(e.target.value)}
            placeholder="$ total" highlight={!!displayTotal} />
          <RowActions onDuplicate={() => onDuplicate(line.id)} onRemove={() => onRemove(line.id)} />
        </div>
        {/* Info strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px',
          padding: '2px 12px 6px 36px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: '#5a2a7a', fontWeight: '600' }}>
            {line.windowCount || '?'} windows · {line.overallWidth}&quot;×{line.overallHeight}&quot;
          </span>
          {line.mullTotal > 0 && <span style={{ fontSize: '11px', color: '#888' }}>Mulls ${parseFloat(line.mullTotal).toFixed(2)}</span>}
          {line.installTotal > 0 && <span style={{ fontSize: '11px', color: '#888' }}>Install ${parseFloat(line.installTotal).toFixed(2)}</span>}
          {line.cutlistWindowsJson && <span style={{ fontSize: '11px', color: '#3b6d11', fontWeight: '600' }}>Cut list ready</span>}
          {line.mullGridJson && <EditConfigBtn onClick={() => onEditConfig(line)} />}
        </div>
      </div>
    );
  }

  // ── ADDER row ─────────────────────────────────────────────────────────────
  // Description spans room+W+H+D (4 cells merged) since adders don't have dimensions
  if (isAdder) {
    return (
      <div style={{ background: rowBg, borderBottom: '0.5px solid #f0ebe4', borderLeft: '3px solid #7d9c4d' }}>
        {/* Use a flex layout for adders so description can naturally span the dimension area */}
        <div style={{ display: 'grid',
          gridTemplateColumns: '20px 88px 58px 1fr 88px 96px 50px',
          gap: '5px', alignItems: 'center', padding: '7px 10px' }}>
          <DragHandle onDragStart={onDragStart} onDragEnd={onDragEnd} />
          <TypeBadge type={line.type} onClick={cycleType} />
          {/* Qty */}
          <input type="number" value={line.qty || ''} min="1" placeholder="1"
            onChange={e => onQtyChange(e.target.value)}
            style={{ ...INP, textAlign: 'center' }} />
          {/* Description — spans the whole middle */}
          <TooltipCombobox value={line.description}
            onChange={v => {
              onChange(line.id, 'description', v);
              // Auto-set adder type when description exactly matches a known type
              // (matches the door → QBO Product auto-fill pattern)
              if (ADDER_TYPES.includes(v.trim())) onChange(line.id, 'adderType', v.trim());
            }}
            suggestions={getSuggestions()} placeholder="Adder description…" />
          {/* Unit price */}
          <MoneyInput value={line.price} onChange={e => onUnitChange(e.target.value)}
            placeholder="$/unit" highlight={!!line.price} />
          {/* Total */}
          <MoneyInput value={displayTotal} onChange={e => onTotalChange(e.target.value)}
            placeholder="$ total" highlight={!!displayTotal} />
          <RowActions onDuplicate={() => onDuplicate(line.id)} onRemove={() => onRemove(line.id)} />
        </div>
        {/* Type sub-strip for adders — locked classification, decoupled from description */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
          padding: '4px 10px 2px 36px' }}>
          <span style={{ fontSize: '11px', color: '#888', fontWeight: '600' }}>Type</span>
          <select value={line.adderType || ''}
            onChange={e => onChange(line.id, 'adderType', e.target.value)}
            style={{ height: '26px', fontSize: '11px', padding: '2px 8px',
              borderRadius: '6px',
              border: `1px solid ${line.adderType ? '#7d9c4d' : '#e4d8cc'}`,
              background: line.adderType ? '#eef5e1' : 'white',
              color: line.adderType ? '#3b6d11' : '#aaa',
              outline: 'none', cursor: 'pointer',
              fontWeight: line.adderType ? '600' : '400' }}>
            <option value="">— Select type —</option>
            {ADDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {!line.adderType && (
            <span style={{ fontSize: '10px', color: '#b89870', fontStyle: 'italic' }}>
              pick a type to categorize this adder
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── WINDOW / DOOR / ORDERED WINDOW row ───────────────────────────────────
  // Full 12-column grid — every field visible
  return (
    <div style={{ background: rowBg, borderBottom: '0.5px solid #f0ebe4',
      borderLeft: isWindow ? `3px solid ${WALNUT}` : '3px solid #d4d0c8' }}>
      <div style={{ display: 'grid', gridTemplateColumns: ROW_GRID, gap: '5px',
        alignItems: 'center', padding: '7px 10px' }}>
        <DragHandle onDragStart={onDragStart} onDragEnd={onDragEnd} />
        <TypeBadge type={line.type} onClick={cycleType} />
        {/* Qty */}
        <input type="number" value={line.qty || ''} min="1" placeholder="1"
          onChange={e => onQtyChange(e.target.value)}
          style={{ ...INP, textAlign: 'center' }} />
        {/* Description */}
        <TooltipCombobox value={line.description} onChange={v => onChange(line.id, 'description', v)}
          suggestions={getSuggestions()} placeholder="Description (shown on estimate)" />
        {/* Room */}
        <TooltipInput value={line.room || ''} onChange={e => onChange(line.id, 'room', e.target.value)}
          placeholder="Room" style={INP} />
        {/* W" */}
        <DimInput value={line.width} onChange={e => onChange(line.id, 'width', e.target.value)} placeholder='W"' />
        {/* H" */}
        <DimInput value={line.height} onChange={e => onChange(line.id, 'height', e.target.value)} placeholder='H"' />
        {/* D" */}
        <DimInput value={line.depth} onChange={e => onChange(line.id, 'depth', e.target.value)} placeholder='D"' />
        {/* UI / Notes */}
        <TooltipInput value={line.ui || ''} onChange={e => onChange(line.id, 'ui', e.target.value)}
          placeholder="Notes / UI" style={INP} />
        {/* Unit price */}
        <MoneyInput value={line.price} onChange={e => onUnitChange(e.target.value)}
          placeholder="$/unit" highlight={!!line.price} />
        {/* Total */}
        <MoneyInput value={displayTotal} onChange={e => onTotalChange(e.target.value)}
          placeholder="$ total" highlight={!!displayTotal} />
        <RowActions onDuplicate={() => onDuplicate(line.id)} onRemove={() => onRemove(line.id)} />
      </div>
      {/* TWP window sub-strip */}
      {isWindow && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
          padding: '2px 10px 6px 36px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: '#888', fontWeight: '600' }}>Type</span>
          <select value={line.windowSubType || ''} onChange={e => onChange(line.id, 'windowSubType', e.target.value)}
            style={{ padding: '3px 8px', border: '0.5px solid #d4d0c8', borderRadius: '5px',
              fontSize: '11px', fontFamily: 'inherit',
              background: line.windowSubType ? '#f5e6d3' : 'white',
              color: line.windowSubType ? '#5a3a1a' : '#aaa',
              outline: 'none', cursor: 'pointer', fontWeight: line.windowSubType ? '600' : '400' }}>
            {TWP_WINDOW_SUBTYPES.map((t, i) => (
              <option key={t.value || `st-${i}`} value={t.value}>{t.label}</option>
            ))}
          </select>
          {!line.windowSubType && (
            <span style={{ fontSize: '11px', color: '#854f0b', fontWeight: '600' }}>set for cut list</span>
          )}
          {line.price && <EditConfigBtn onClick={() => onEditConfig(line)} />}
        </div>
      )}
      {/* Ordered Window QBO Product sub-strip — locks QBO binding independent of description */}
      {isOrdered && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
          padding: '2px 10px 6px 36px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: '#888', fontWeight: '600' }}>QBO Product</span>
          <select value={line.orderedProduct || ''} onChange={e => onChange(line.id, 'orderedProduct', e.target.value)}
            style={{ padding: '3px 8px', border: '0.5px solid #c8d0d4', borderRadius: '5px',
              fontSize: '11px', fontFamily: 'inherit',
              background: line.orderedProduct ? '#dbe7f3' : 'white',
              color: line.orderedProduct ? '#1a3a5a' : '#aaa',
              outline: 'none', cursor: 'pointer', fontWeight: line.orderedProduct ? '600' : '400' }}>
            {ORDERED_PRODUCT_OPTIONS.map((p, i) => (
              <option key={p.value || `op-${i}`} value={p.value}>{p.label}</option>
            ))}
          </select>
          {!line.orderedProduct && (
            <span style={{ fontSize: '11px', color: '#0b5485', fontWeight: '600' }}>lock for QBO sync</span>
          )}
        </div>
      )}
      {/* Door QBO Product sub-strip — locks QBO binding independent of description */}
      {isDoor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
          padding: '2px 10px 6px 36px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: '#888', fontWeight: '600' }}>QBO Product</span>
          <select value={line.doorProduct || ''} onChange={e => onChange(line.id, 'doorProduct', e.target.value)}
            style={{ padding: '3px 8px', border: '0.5px solid #d4c8e0', borderRadius: '5px',
              fontSize: '11px', fontFamily: 'inherit',
              background: line.doorProduct ? '#ebe3f5' : 'white',
              color: line.doorProduct ? '#3a1a5a' : '#aaa',
              outline: 'none', cursor: 'pointer', fontWeight: line.doorProduct ? '600' : '400' }}>
            {DOOR_PRODUCT_OPTIONS.map((p, i) => (
              <option key={p.value || `dp-${i}`} value={p.value}>{p.label}</option>
            ))}
          </select>
          {!line.doorProduct && (
            <span style={{ fontSize: '11px', color: '#54087a', fontWeight: '600' }}>lock for QBO sync</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Drag-to-reorder list ──────────────────────────────────────────────────────
function DraggableLineList({ lines, onChange, onRemove, onDuplicate, onEditConfig, onReorder }) {
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const dragIndex = useRef(null);

  const handleDragStart = (e, idx) => { dragIndex.current = idx; setDragging(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver  = (e, idx) => { e.preventDefault(); if (idx !== dragIndex.current) setDragOver(idx); };
  const handleDrop      = (e, idx) => {
    e.preventDefault();
    if (dragIndex.current !== null && dragIndex.current !== idx) onReorder(dragIndex.current, idx);
    setDragging(null); setDragOver(null); dragIndex.current = null;
  };
  const handleDragEnd   = () => { setDragging(null); setDragOver(null); dragIndex.current = null; };

  return (
    <>
      {lines.map((line, i) => (
        <div key={line.id || i}
          // The wrapper is only a DROP target — it doesn't initiate drags.
          // Drag is initiated by the grip handle inside LineRow (left edge).
          // This lets users select text inside inputs/notes without the row jumping.
          onDragOver={e => handleDragOver(e, i)}
          onDrop={e => handleDrop(e, i)}
          style={{
            opacity: dragging === i ? 0.45 : 1,
            background: dragOver === i ? '#f0e8e0' : undefined,
            outline: dragOver === i ? `2px dashed ${WALNUT}88` : 'none',
            outlineOffset: '-2px',
            transition: 'opacity 0.15s',
          }}>
          <LineRow line={line} onChange={onChange} onRemove={onRemove}
            onDuplicate={onDuplicate} onEditConfig={onEditConfig}
            onDragStart={e => handleDragStart(e, i)}
            onDragEnd={handleDragEnd} />
        </div>
      ))}
    </>
  );
}

// Plain-English save status so reps can see their work is kept without clicking anything
function AutoSaveStatus({ state, savedAt, hasDoc, onRetry }) {
  const base = { fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' };
  if (state === 'error') {
    return (
      <span style={{ ...base, color: RUST }}>
        {"Couldn't save — check your internet."}
        <button onClick={onRetry} style={{ background: 'none', border: `1px solid ${RUST}`, color: RUST, borderRadius: '5px',
          padding: '1px 8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS }}>Try again</button>
      </span>
    );
  }
  if (state === 'pending' || state === 'saving') {
    return <span style={{ ...base, color: STONE }}>Saving…</span>;
  }
  if (!hasDoc) {
    return <span style={{ ...base, color: STONE, fontWeight: '500' }}>This quote saves automatically as you type</span>;
  }
  const time = savedAt ? savedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;
  return (
    <span style={{ ...base, color: MOSS }}>
      ✓ Saved automatically{time ? ` at ${time}` : ''} — find it anytime under Estimates
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function QuickQuotePage() {
  const confirm = useConfirm();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const editId         = routeId || searchParams.get('edit');
  const location       = useLocation();
  const { currentUser, userProfile } = useAuth();

  const [saving, setSaving]               = useState(false);
  const [previewBlob, setPreviewBlob]     = useState(null);
  const [estimateDocId, setEstimateDocId] = useState(null);
  const [estimateStatus, setEstimateStatus] = useState(null);
  const [sending, setSending]             = useState(false);

  // ── Auto-save ──────────────────────────────────────────────────────────────
  // Every change is written to Firestore ~1.5s after the user stops typing, so
  // a rep who closes the tab or walks away never loses a quote. Auto-save only
  // writes data fields — it never changes status or owner (Save Draft / Send /
  // Approve still do that).
  const estimateDocIdRef  = useRef(null);
  const autoSaveInFlight  = useRef(null);
  const dirtyRef          = useRef(false);
  const skipNextAutoSave  = useRef(true);   // ignore the render that loads/initializes the form
  const [loaded, setLoaded]               = useState(!editId);
  const [autoSaveState, setAutoSaveState] = useState('idle'); // idle | pending | saving | saved | error
  const [lastSavedAt, setLastSavedAt]     = useState(null);

  // ── Ownership / Assignee state ─────────────────────────────────────────────
  // assignees: list of users who can own an estimate (sales, officeManager, admin, manager)
  // assigneeUid: current selection (the rep this estimate "belongs to")
  // originalOwner: snapshot of who owned it BEFORE any reassignment (for confirmation prompt)
  const [assignees, setAssignees]               = useState([]);
  const [assigneeUid, setAssigneeUid]           = useState(null);
  const [originalOwnerUid, setOriginalOwnerUid] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sentSuccess, setSentSuccess]     = useState(false);
  const [editConfig, setEditConfig]       = useState(null);

  const prefill = location.state?.prefillCustomer || {};
  const [customer, setCustomer] = useState({
    name: prefill.name || '', address: prefill.address || '', city: prefill.city || '',
    state: prefill.state || 'SD', zip: prefill.zip || '',
    phone: prefill.phone || '', cellPhone: prefill.cellPhone || '',
    workPhone: prefill.workPhone || '', email: prefill.email || '',
  });
  const [orderDate, setOrderDate] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  });
  const [notes, setNotes]           = useState('');
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate]       = useState('2.041');
  const [lines, setLines]           = useState([EMPTY_LINE()]);

  useEffect(() => {
    if (!editId) return;
    getEstimate(editId).then(est => {
      if (!est) return;
      setEstimateDocId(editId);
      setCustomer(est.customer || {});
      const n = new Date();
      setOrderDate(est.orderDate || `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`);
      setNotes(est.notes || '');
      setTaxEnabled(est.taxEnabled || false);
      setTaxRate(est.taxRate || '2.041');
      // Backfill qboName on every loaded line. For lines created before
      // qboName existed, this populates it. For lines that already have one,
      // this keeps it current with the latest mapping table.
      const loadedLines = est.lines || [EMPTY_LINE()];
      setLines(loadedLines.map(l => {
        const next = { ...l };
        if (next.type === 'ordered_window' && !next.orderedProduct) {
          const detected = detectOrderedProductFromDescription(next.description);
          if (detected) next.orderedProduct = detected;
        }
        if (next.type === 'door' && !next.doorProduct) {
          const detected = detectDoorProductFromDescription(next.description);
          if (detected) next.doorProduct = detected;
        }
        next.qboName = computeQboName(next);
        return next;
      }));
      setEstimateStatus(est.status || null);
      // Capture current owner — this is what gets shown in the Assignee picker for existing estimates.
      // For legacy estimates with no owner, this stays null and the picker will default to the current user.
      setOriginalOwnerUid(est.createdByUid || null);
      setAssigneeUid(est.createdByUid || null);
      estimateDocIdRef.current = editId;
      setLastSavedAt(est.updatedAt?.toDate ? est.updatedAt.toDate() : null);
      setLoaded(true);
    });
  }, [editId]);

  // Load list of assignable users for the picker — only if user role permits
  useEffect(() => {
    const role = userProfile?.role;
    const canReassign = role === 'officeManager' || role === 'admin' || role === 'manager';
    if (!canReassign) return;
    getAssignableUsers().then(users => {
      setAssignees(users);
      // For NEW estimates, default the assignee to the current user
      // (Amy creating a new estimate defaults to herself)
      if (!editId && !assigneeUid && currentUser?.uid) {
        setAssigneeUid(currentUser.uid);
      }
    }).catch(e => console.error('Failed to load assignees:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.role, currentUser?.uid, editId]);

  // Helper — resolve the assignee data (the user who OWNS this estimate)
  function resolveAssignee() {
    // Look up in our loaded assignees list first
    if (assigneeUid) {
      const found = assignees.find(u => u.uid === assigneeUid);
      if (found) return found;
    }
    // Fallback: current user (for salespersons creating their own, or if list hasn't loaded yet)
    return {
      uid:               currentUser?.uid || null,
      displayName:       userProfile?.displayName || userProfile?.name || 'Unknown',
      email:             currentUser?.email || userProfile?.email || '',
      displayPhone:      userProfile?.displayPhone || '605-432-1834',
      notificationPhone: userProfile?.notificationPhone || null,
      role:              userProfile?.role || '',
    };
  }

  const updateLine = useCallback((id, fieldOrObj, value) => {
    setLines(prev => prev.map(l => {
      if (l.id !== id) return l;
      const next = typeof fieldOrObj === 'object'
        ? { ...l, ...fieldOrObj }
        : { ...l, [fieldOrObj]: value };
      // Auto-detect & lock orderedProduct from description on first input.
      // Once locked, the description field is fully editable for customer-facing
      // text without breaking the QBO sync binding.
      if (next.type === 'ordered_window' && !next.orderedProduct) {
        const detected = detectOrderedProductFromDescription(next.description);
        if (detected) next.orderedProduct = detected;
      }
      if (next.type === 'door' && !next.doorProduct) {
        const detected = detectDoorProductFromDescription(next.description);
        if (detected) next.doorProduct = detected;
      }
      // Recompute qboName every time the line changes so it stays in sync
      // with description / windowSubType / color / handing. Persists into
      // Firestore on save → travels through to QBO invoice sync.
      next.qboName = computeQboName(next);
      return next;
    }));
  }, []);

  const removeLine = useCallback((id) => {
    setLines(prev => prev.length > 1 ? prev.filter(l => l.id !== id) : prev);
  }, []);

  const duplicateLine = useCallback((id) => {
    setLines(prev => {
      const idx = prev.findIndex(l => l.id === id);
      if (idx === -1) return prev;
      const copy = { ...prev[idx], id: Date.now() + Math.random() };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }, []);

  const reorderLines = useCallback((fromIdx, toIdx) => {
    setLines(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, []);

  const addLine = (type = 'twp_window') =>
    setLines(prev => [...prev, { ...EMPTY_LINE(), id: Date.now() + Math.random(), type }]);

  const handleEditConfig = useCallback((line) => {
    if (line.isMulledUnit) {
      let grid = null;
      try { grid = JSON.parse(line.mullGridJson); } catch { /* handled by the alert below */ }
      if (!grid) { alert('No stored configuration found.'); return; }
      setEditConfig({ lineId: line.id, type: 'mulled', config: { grid, overallWidth: String(line.overallWidth || ''), overallHeight: String(line.overallHeight || ''), room: line.room || '' } });
    } else {
      const windowType = line.twpConfig?.windowType || SUBTYPE_TO_TYPE[line.windowSubType] || 'Double Hung';
      setEditConfig({ lineId: line.id, type: 'single', config: { windowType, variant: line.twpConfig?.variant || 'White',
          width: line.width || '', height: line.height || '',
          keeperCfg: line.twpConfig?.glassConfig?.keeper || null,
          lockCfg:   line.twpConfig?.glassConfig?.lock   || null,
          installFee: line.twpConfig?.installFee ?? 250 , room: line.room || ''} });
    }
  }, []);

  const subtotal   = lines.filter(l => l.type !== 'note' && l.price).reduce((s, l) => s + (parseFloat(l.price)||0)*(parseFloat(l.qty)||1), 0);
  const taxAmount  = taxEnabled ? subtotal * (parseFloat(taxRate) / 100) : 0;
  const grandTotal = subtotal + taxAmount;

  // Latest form values, read by auto-save so a delayed/unmount save never uses stale state
  const latestForm = useRef(null);
  latestForm.current = { customer, orderDate, notes, taxEnabled, taxRate, lines, subtotal, taxAmount, grandTotal };

  const runAutoSave = async () => {
    if (autoSaveInFlight.current) await autoSaveInFlight.current;   // one write at a time — never double-create
    if (!dirtyRef.current) return;
    const f = latestForm.current;
    const hasContent = (f.customer?.name || '').trim()
      || f.lines.some(l => (l.description || '').trim() || parseFloat(l.price) > 0);
    // Don't create blank estimates just because someone opened New Quote
    if (!estimateDocIdRef.current && !hasContent) { setAutoSaveState('idle'); return; }

    dirtyRef.current = false;
    setAutoSaveState('saving');
    const dataFields = {
      customer: f.customer, orderDate: f.orderDate, notes: f.notes,
      taxEnabled: f.taxEnabled, taxRate: f.taxRate,
      taxType: !f.taxEnabled ? 'none' : f.taxRate === '6.2' ? 'sales' : 'excise',
      lines: f.lines, subtotal: f.subtotal, taxAmount: f.taxAmount, grandTotal: f.grandTotal,
    };
    const write = (async () => {
      if (estimateDocIdRef.current) {
        await updateEstimate(estimateDocIdRef.current, dataFields);
      } else {
        const owner = resolveAssignee();
        const r = await createEstimate({
          ...dataFields,
          status:         'draft',
          createdBy:      owner.email,
          createdByName:  owner.displayName,
          createdByPhone: owner.displayPhone,
          createdByUid:   owner.uid,
          enteredByUid:   currentUser?.uid || null,
          enteredByName:  userProfile?.displayName || userProfile?.name || '',
          enteredByEmail: currentUser?.email || '',
        });
        estimateDocIdRef.current = r.id;
        setEstimateDocId(r.id);
        setOriginalOwnerUid(owner.uid);
        setEstimateStatus('draft');
      }
    })();
    autoSaveInFlight.current = write;
    try {
      await write;
      setLastSavedAt(new Date());
      setAutoSaveState(dirtyRef.current ? 'pending' : 'saved');
    } catch (e) {
      console.error('Auto-save failed:', e);
      dirtyRef.current = true;
      setAutoSaveState('error');
    } finally {
      autoSaveInFlight.current = null;
    }
  };
  const runAutoSaveRef = useRef(runAutoSave);
  runAutoSaveRef.current = runAutoSave;

  // Debounced save whenever the form changes
  useEffect(() => {
    if (!loaded) return;
    if (skipNextAutoSave.current) { skipNextAutoSave.current = false; return; }
    dirtyRef.current = true;
    setAutoSaveState('pending');
    const t = setTimeout(() => runAutoSaveRef.current(), 1500);
    return () => clearTimeout(t);
  }, [loaded, customer, orderDate, notes, taxEnabled, taxRate, lines]);

  // Leaving the page (Back button, sidebar link) — save whatever is pending
  useEffect(() => () => { if (dirtyRef.current) runAutoSaveRef.current(); }, []);

  // Closing the tab within the 1.5s window — save and ask the browser to wait
  useEffect(() => {
    function onBeforeUnload(e) {
      if (!dirtyRef.current && !autoSaveInFlight.current) return;
      runAutoSaveRef.current();
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const handleSave = async (status = 'draft') => {
    // ── QBO Product / Type validation (Option C) ───────────────────────────────
    // Non-draft saves require every populated row to have its type picked:
    //   - TWP window: windowSubType set (Double Hung, Slider, etc.)
    //   - ordered_window: orderedProduct picked (Minnkota / ProVia / Vector / Sash / Other)
    //   - door: doorProduct picked (Exterior / Patio / Storm / Side Lites / Other)
    // "Other" is a valid pick — it explicitly tells the system to fall back to
    // the generic "TWP Services" item in QBO for one-offs.
    // Empty rows (no description AND no price) are placeholders and skipped.
    if (status !== 'draft') {
      const missingRows = [];
      lines.forEach((line, idx) => {
        const hasContent = (line.description && line.description.trim()) ||
                           (line.price && parseFloat(line.price) > 0) ||
                           (line.totalPrice && parseFloat(line.totalPrice) > 0);
        if (!hasContent) return;
        const label = `Row ${idx + 1} — "${(line.description || '').trim() || '(no description)'}"`;
        if (line.type === 'twp_window' && !line.windowSubType) {
          missingRows.push(`${label} — TWP window needs Type picked (Double Hung, Slider, etc.)`);
        }
        if (line.type === 'ordered_window' && !line.orderedProduct) {
          missingRows.push(`${label} — Ordered window needs QBO Product picked (Minnkota / ProVia / Vector / Sash / Other)`);
        }
        if (line.type === 'door' && !line.doorProduct) {
          missingRows.push(`${label} — Door needs QBO Product picked (Exterior / Patio / Storm / Side Lites / Other)`);
        }
      });
      if (missingRows.length > 0) {
        window.alert(
          `Cannot save — ${missingRows.length} row${missingRows.length === 1 ? '' : 's'} need attention:\n\n` +
          missingRows.join('\n') +
          `\n\nFix each row by picking its Type/QBO Product (use "Other / Custom" for one-offs), then save again.`
        );
        return;
      }
    }

    setSaving(true);
    // Owner fields are computed from the assignee — NOT from the logged-in user.
    // For salespersons (no picker), assigneeUid is null and resolveAssignee falls back to currentUser.
    // For officeManager / admin, assigneeUid comes from the picker.
    const owner = resolveAssignee();

    // Always-safe fields (data fields that any save touches)
    const dataFields = {
      customer, orderDate, notes, taxEnabled, taxRate,
      taxType: !taxEnabled ? 'none' : taxRate === '6.2' ? 'sales' : 'excise',
      lines, status, subtotal, taxAmount, grandTotal,
      ...(status === 'approved' ? { approvedAt: new Date() } : {}),
    };

    // Let any auto-save finish first so we update its doc instead of creating a second one
    if (autoSaveInFlight.current) { try { await autoSaveInFlight.current; } catch { /* surfaced by auto-save status */ } }
    let savedId = estimateDocIdRef.current;
    try {
      if (savedId) {
        // UPDATE — never overwrite owner fields unless assignee changed via picker
        const ownerChanged = assigneeUid && assigneeUid !== originalOwnerUid;
        const updates = { ...dataFields };
        if (ownerChanged) {
          updates.createdByUid   = owner.uid;
          updates.createdByName  = owner.displayName;
          updates.createdByPhone = owner.displayPhone;
          updates.createdBy      = owner.email;
          // Keep originalOwnerUid in sync so subsequent saves don't re-trigger
          setOriginalOwnerUid(owner.uid);
        }
        await updateEstimate(savedId, updates);
      } else {
        // CREATE — stamp owner fields based on the assignee (for officeManager picker)
        // or the current user (for salespersons with no picker).
        // ALSO stamp enteredByUid: the user who actually keyed in the data, immutable forever.
        const createData = {
          ...dataFields,
          createdBy:        owner.email,
          createdByName:    owner.displayName,
          createdByPhone:   owner.displayPhone,
          createdByUid:     owner.uid,
          enteredByUid:     currentUser?.uid || null,
          enteredByName:    userProfile?.displayName || userProfile?.name || '',
          enteredByEmail:   currentUser?.email || '',
        };
        const r = await createEstimate(createData);
        savedId = r.id;
        estimateDocIdRef.current = r.id;
        setEstimateDocId(r.id);
        setOriginalOwnerUid(owner.uid);
      }
      setEstimateStatus(status);
      dirtyRef.current = false;
      setLastSavedAt(new Date());
      setAutoSaveState('saved');
      setSaving(false);
      return savedId;
    } catch (e) { alert('Error saving.'); console.error(e); }
    setSaving(false);
    return null;
  };

  // Approve directly from the editor: marks the estimate Approved and jumps
  // straight to Job Order creation, skipping the extra trip through the
  // Estimates list / Customer Hub to find and approve it there.
  const handleApproveAndCreateJobOrder = async () => {
    if (!(await confirm({
      title: 'Approve this estimate?',
      message: 'This marks it Approved and takes you straight to the Job Order page.',
      confirmText: 'Approve & Continue',
    }))) return;
    const id = await handleSave('approved');
    if (id) navigate(`/estimates/job-order/${id}`);
  };

  const handleSendToCustomer = async () => {
    if (!customer.email && !customer.phone && !customer.cellPhone) {
      alert('Add a customer email or phone before sending.'); return;
    }
    // Make sure the latest edits are saved before the PDF/link goes out
    dirtyRef.current = true;
    await runAutoSave();
    const estimateDocId = estimateDocIdRef.current;
    if (!estimateDocId) { alert('Add a customer name or a line item first.'); return; }
    setSending(true);
    try {
      const publicToken = crypto.randomUUID();
      // The PDF "Prepared By" block reads from the OWNER of this estimate, not the logged-in user.
      // If Amy clicks Send on AJ's estimate, the PDF still says "Prepared by AJ".
      const owner = resolveAssignee();
      const pdfBlob = await generateQuotePDFBlob(
        { customer, lines, subtotal, taxAmount, grandTotal, taxEnabled, taxRate, notes, orderDate },
        { displayName:       owner.displayName,
          displayPhone:      owner.displayPhone,
          email:             owner.email,
          notificationPhone: owner.notificationPhone || '' }
      );
      const { uploadEstimatePDF } = await import('../../services/pdf-storage');
      const pdfUrl = await uploadEstimatePDF(estimateDocId, pdfBlob);
      // Only update fields that the send action actually changes — DO NOT touch ownership fields.
      await updateEstimate(estimateDocId, {
        publicToken, pdfUrl, status: 'sent', sentAt: new Date().toISOString(),
        sentTo: { email: customer.email || null, phone: customer.phone || customer.cellPhone || null },
      });
      setEstimateStatus('sent');
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { app } = await import('../../services/firebase-init');
      await httpsCallable(getFunctions(app), 'sendEstimateToCustomer')({ estimateId: estimateDocId, publicToken, pdfUrl });
      setSentSuccess(true);
    } catch (e) { console.error(e); alert('Error sending estimate.'); }
    setSending(false);
  };

  const handleUsePrice = useCallback((price, meta) => {
    const editLineId = editConfig?.lineId || null;
    // EDIT MODE: update the existing row in place.
    // CREATE MODE: always APPEND a new TWP window row, regardless of what the
    // last row is (adder, note, ordered window, nothing). Never silently fail.
    let targetId;
    let isNewRow = false;
    if (editLineId) {
      targetId = editLineId;
    } else {
      // Look for an empty TWP window row at the END of the list to fill in.
      // "Empty" = type twp_window AND no description, price, width, or height.
      // If found, fill it. Otherwise APPEND a new row.
      const newQty = String(parseInt(meta?.qty) || 1);
      const isMulled = !!meta?.isMulledUnit;
      const lastIdx = lines.length - 1;
      const last = lastIdx >= 0 ? lines[lastIdx] : null;
      const isLastEmptyTwp = last
        && last.type === 'twp_window'
        && !last.description
        && !last.price
        && !last.width
        && !last.height
        && !last.isMulledUnit;

      if (isLastEmptyTwp) {
        // Fill the existing empty TWP row in place
        targetId = last.id;
        const fillUpd = {
          qty: newQty,
          windowSubType: isMulled ? 'mulled' : (meta?.windowType && SUBTYPE_MAP[meta.windowType]) || '',
        };
        updateLine(targetId, fillUpd);
        isNewRow = true;
      } else {
        // Append a new row — stamp qboName at creation time so the
        // line carries its QBO mapping forever.
        const newId = Date.now() + Math.random();
        const newLine = {
          ...EMPTY_LINE(),
          id: newId,
          type: 'twp_window',
          qty: newQty,
          windowSubType: isMulled ? 'mulled' : (meta?.windowType && SUBTYPE_MAP[meta.windowType]) || '',
        };
        newLine.qboName = computeQboName(newLine);
        setLines(prev => [...prev, newLine]);
        targetId = newId;
        isNewRow = true;
      }
    }
    const qty = parseInt(meta?.qty) || (isNewRow ? 1 : (parseFloat(lines.find(l => l.id === targetId)?.qty) || 1));
    const total = (price * qty).toFixed(2);
    if (meta?.isMulledUnit) {
      // Existing values on the row being filled (empty for a brand-new row)
      const tl = lines.find(l => l.id === targetId) || {};
      const mulledUpd = {
        price: String(price), totalPrice: total,
        description: meta.description || tl.description,
        width: meta.overallWidth ? String(meta.overallWidth) : tl.width,
        height: meta.overallHeight ? String(meta.overallHeight) : tl.height,
        windowSubType: 'mulled', isMulledUnit: true,
        mullGridJson: JSON.stringify(meta.grid),
        cutlistWindowsJson: JSON.stringify(meta.cutlistWindows),
        mullTotal: meta.mullTotal, installTotal: meta.installTotal,
        windowCount: meta.windowCount,
        overallWidth: meta.overallWidth, overallHeight: meta.overallHeight,
      };
      // Only stamp room if user typed something (skip leaves it as-is)
      if (typeof meta.room === 'string' && meta.room.trim()) {
        mulledUpd.room = meta.room.trim();
      }
      if (meta?.qty) mulledUpd.qty = String(parseInt(meta.qty) || 1);
      updateLine(targetId, mulledUpd);
    } else {
      const upd = {
        price: String(price), totalPrice: total,
        twpConfig: {
          windowType: meta?.windowType || '', variant: meta?.variant || '',
          glassConfig: meta?.glassConfig || null,
          gridPrice: meta?.gridPrice || 0, installFee: meta?.installFee || 0,
        },
      };
      if (meta?.width)       upd.width       = meta.width;
      if (meta?.height)      upd.height      = meta.height;
      if (meta?.description) upd.description = meta.description;
      if (meta?.windowType && SUBTYPE_MAP[meta.windowType]) upd.windowSubType = SUBTYPE_MAP[meta.windowType];
      // Only stamp room if user typed something (skip leaves existing room as-is)
      if (typeof meta.room === 'string' && meta.room.trim()) upd.room = meta.room.trim();
      if (meta?.qty) upd.qty = String(parseInt(meta.qty) || 1);
      updateLine(targetId, upd);
    }
    setEditConfig(null);
  }, [lines, updateLine, editConfig]);

  const taxOptions = [
    { value: 'excise', label: 'Excise 2.041%', rate: '2.041' },
    { value: 'sales',  label: 'Sales Tax 6.2%',   rate: '6.2' },
    { value: 'none',   label: 'No Tax',            rate: '0' },
  ];

  const breadcrumb = routeId ? 'Dashboard › Estimates › Edit Quote' : 'Dashboard › Estimates › New Quote';

  return (
    <AppLayout
      title={routeId ? 'Edit Quote' : 'New Quote'}
      breadcrumb={breadcrumb}
      topBarActions={
        <>
          <Button variant="secondary" size="small" onClick={() => handleSave('draft')} disabled={saving}>
            {saving ? 'Saving…' : 'Save Draft'}
          </Button>
          {estimateStatus === 'approved' ? (
            <Button variant="secondary" size="small" onClick={() => navigate(`/estimates/contract/${estimateDocId}`)}>
              Create Contract
            </Button>
          ) : (
            <Button variant="secondary" size="small" onClick={handleApproveAndCreateJobOrder} disabled={saving}>
              Approve & Create Job Order
            </Button>
          )}
          <Button size="small" onClick={() => setShowSendModal(true)} disabled={saving || sending}>
            Send to Customer
          </Button>
        </>
      }
    >
    <div style={{ fontFamily: FONT_SANS }}>

      {/* Back to Estimates */}
      <button
        onClick={() => navigate('/estimates')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          background: 'white', border: `1.5px solid ${WALNUT}`, color: WALNUT,
          borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          fontSize: '15px', fontWeight: '700', cursor: 'pointer',
          fontFamily: FONT_SANS, padding: '10px 18px', marginBottom: '16px',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = WALNUT; e.currentTarget.style.color = 'white'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = WALNUT; }}
      >
        <ArrowLeft size={18} strokeWidth={2.5} /> Back to all Estimates
      </button>

      {/* Status + saved indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', color: STONE }}>Customer-facing estimate</span>
        {estimateStatus === 'sent'              && <span style={{ fontSize:'11px',fontWeight:'600',color:OCHRE,background:'#FBF1E0',padding:'3px 10px',borderRadius:'5px' }}>Sent</span>}
        {estimateStatus === 'approved'          && <span style={{ fontSize:'11px',fontWeight:'600',color:MOSS,background:'#EAF1EA',padding:'3px 10px',borderRadius:'5px' }}>Approved</span>}
        {estimateStatus === 'changes_requested' && <span style={{ fontSize:'11px',fontWeight:'600',color:RUST,background:'#FBEBE8',padding:'3px 10px',borderRadius:'5px' }}>Changes Requested</span>}
        {estimateStatus === 'contract_signed'   && <span style={{ fontSize:'11px',fontWeight:'600',color:MOSS,background:'#EAF1EA',padding:'3px 10px',borderRadius:'5px' }}>Signed</span>}
        <AutoSaveStatus state={autoSaveState} savedAt={lastSavedAt} hasDoc={!!estimateDocId}
          onRetry={() => { dirtyRef.current = true; runAutoSave(); }} />
      </div>

      {/* ── Assignee bar (officeManager / admin / manager only) ── */}
      {(['officeManager', 'admin', 'manager'].includes(userProfile?.role)) && (
        <div style={{
          background: PAPER,
          border: '1px solid #e8e2da',
          borderRadius: '10px',
          padding: '12px 16px',
          marginBottom: '14px',
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: STONE }}>Assigned to</div>
          <select
            name="twp-quote-assignee"
            autoComplete="off"
            value={assigneeUid || ''}
            onChange={async (e) => {
              const newUid = e.target.value;
              if (originalOwnerUid && newUid !== originalOwnerUid) {
                const newOwner = assignees.find(u => u.uid === newUid);
                const oldOwner = assignees.find(u => u.uid === originalOwnerUid);
                const oldName = oldOwner?.displayName || 'the previous owner';
                const newName = newOwner?.displayName || 'this user';
                if (!(await confirm({ title: `Reassign from ${oldName} to ${newName}?`, message: `The new owner will see it on their dashboard. The PDF "Prepared By" will update to ${newName}.`, confirmText: 'Reassign' }))) {
                  return;
                }
              }
              setAssigneeUid(newUid || null);
            }}
            style={{
              padding: '7px 12px', borderRadius: '7px',
              border: '1px solid #e0d5cc',
              background: 'white', color: '#2d3748',
              fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', fontFamily: FONT_SANS,
              minWidth: '220px',
            }}
          >
            {assignees.length === 0 && <option value="">Loading...</option>}
            {assignees.map(u => (
              <option key={u.uid} value={u.uid}>
                {u.displayName} {u.role === 'officeManager' ? '(Office Manager)' : u.role === 'sales' ? '(Sales)' : ''}
              </option>
            ))}
          </select>
          {originalOwnerUid && assigneeUid && assigneeUid !== originalOwnerUid && (
            <span style={{
              fontSize: '11px', fontWeight: '700',
              color: OCHRE, background: '#FBF1E0',
              padding: '4px 10px', borderRadius: '20px',
            }}>Reassignment Pending — Save to Confirm</span>
          )}
          <span style={{ fontSize: '11px', color: STONE, marginLeft: 'auto' }}>
            The PDF &ldquo;Prepared By&rdquo; reflects whoever this estimate is assigned to.
          </span>
        </div>
      )}

      {/* ── Two-column body ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '0 0 100px' }}>

        {/* LEFT — main content */}
        <div style={{ flex: '1 1 0', minWidth: 0, marginRight: '16px' }}>

          {/* Customer info — real <form autocomplete="off"> so Chrome does not
             offer to "save address" when the estimate is approved / navigated. */}
          <form onSubmit={e => e.preventDefault()} autoComplete="off" {...noAutofillContainer}
            style={{ background: 'white', borderRadius: '10px', padding: '18px 20px',
            marginBottom: '14px', border: '0.5px solid #e8e2da' }}>
            <input type="text" name="twp-qq-antifill" tabIndex={-1} aria-hidden="true"
              autoComplete="off" style={{ display: 'none' }} />
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '14px',
              textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 148px', gap: '9px', marginBottom: '9px' }}>
              <F label="Name"><input name="twp-customer-name" {...noAutofill} value={customer.name} onChange={e=>setCustomer(p=>({...p,name:e.target.value}))} placeholder="Full name" style={INP}/></F>
              <F label="Home Phone"><input name="twp-customer-home-phone" {...noAutofill} value={customer.phone} onChange={e=>setCustomer(p=>({...p,phone:e.target.value}))} placeholder="(605) 000-0000" style={INP}/></F>
              <F label="Cell Phone"><input name="twp-customer-cell-phone" {...noAutofill} value={customer.cellPhone} onChange={e=>setCustomer(p=>({...p,cellPhone:e.target.value}))} placeholder="(605) 000-0000" style={INP}/></F>
              <F label="Work Phone"><input name="twp-customer-work-phone" {...noAutofill} value={customer.workPhone} onChange={e=>setCustomer(p=>({...p,workPhone:e.target.value}))} placeholder="(605) 000-0000" style={INP}/></F>
              <F label="Date"><input type="date" value={orderDate} onChange={e=>setOrderDate(e.target.value)} style={INP}/></F>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 58px 88px 1.8fr', gap: '9px' }}>
              <F label="Address"><input name="twp-customer-address" {...noAutofill} value={customer.address} onChange={e=>setCustomer(p=>({...p,address:e.target.value}))} placeholder="Street address" style={INP}/></F>
              <F label="City"><input name="twp-customer-city" {...noAutofill} value={customer.city} onChange={e=>setCustomer(p=>({...p,city:e.target.value}))} placeholder="City" style={INP}/></F>
              <F label="State"><input name="twp-customer-state" {...noAutofill} value={customer.state} onChange={e=>setCustomer(p=>({...p,state:e.target.value}))} placeholder="SD" style={INP}/></F>
              <F label="Zip"><input name="twp-customer-zip" {...noAutofill} value={customer.zip} onChange={e=>setCustomer(p=>({...p,zip:e.target.value}))} placeholder="57252" style={INP}/></F>
              <F label="Email"><input name="twp-customer-email" {...noAutofill} value={customer.email} onChange={e=>setCustomer(p=>({...p,email:e.target.value}))} placeholder="customer@email.com" style={INP}/></F>
            </div>
          </form>

          {/* Line items card */}
          <div style={{ background: 'white', borderRadius: '10px',
            border: '0.5px solid #e8e2da', overflow: 'hidden' }}>

            {/* Column headers — match ROW_GRID exactly */}
            <div style={{ display: 'grid', gridTemplateColumns: ROW_GRID, gap: '5px',
              padding: '8px 10px', background: '#f9f8f5', borderBottom: '0.5px solid #e8e2da' }}>
              {['', 'Type', 'Qty', 'Description', 'Room', 'W"', 'H"', 'D"', 'UI / Notes', '$/Unit', 'Total', ''].map((h, i) => (
                <div key={i} style={{ fontSize: '10px', fontWeight: '600', color: '#999',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  textAlign: ['$/Unit','Total'].includes(h) ? 'right' : 'center',
                  paddingRight: ['$/Unit','Total'].includes(h) ? '4px' : 0,
                  paddingLeft: ['Description','UI / Notes','Room'].includes(h) ? '2px' : 0,
                }}>{h}</div>
              ))}
            </div>

            {/* Rows */}
            <DraggableLineList
              lines={lines} onChange={updateLine} onRemove={removeLine}
              onDuplicate={duplicateLine} onEditConfig={handleEditConfig}
              onReorder={reorderLines}
            />

            {/* Add row buttons */}
            <div style={{ padding: '10px 10px', display: 'flex', gap: '6px', flexWrap: 'wrap',
              background: '#f9f8f5', borderTop: '0.5px solid #e8e2da', alignItems: 'center' }}>
              {LINE_TYPES.map(({ value, label, color, bg }, i) => (
                <button key={value || i} onClick={() => addLine(value)}
                  style={{ padding: '6px 13px', background: bg, color,
                    border: 'none', borderRadius: '7px',
                    fontSize: '12px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'opacity 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                  + {label}
                </button>
              ))}
              <span style={{ fontSize: '11px', color: '#bbb', marginLeft: '8px' }}>Drag to reorder</span>
            </div>
          </div>

          {/* Payment terms note */}
          <div style={{ display: 'flex', gap: '20px', padding: '14px 6px', flexWrap: 'wrap' }}>
            {[['Windows','1/3 Down'],['Doors','1/2 Down'],['Balance','Upon Completion']].map(([label,val])=>(
              <div key={label} style={{ display:'flex',alignItems:'center',gap:'6px' }}>
                <span style={{ fontSize:'11px',color:'#999',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.4px' }}>{label}</span>
                <span style={{ fontSize:'12px',color:'#555' }}>{val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT SIDEBAR — sticky totals, notes, PDF ── */}
        <div style={{ width: '310px', flexShrink: 0, position: 'sticky', top: '70px',
          display: 'flex', flexDirection: 'column', gap: '11px' }}>

          {/* Totals card */}
          <div style={{ background: 'white', borderRadius: '10px', padding: '18px 20px',
            border: '0.5px solid #e8e2da' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '14px',
              textTransform: 'uppercase', letterSpacing: '0.5px' }}>Totals</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '9px',
              paddingBottom: '9px', borderBottom: '1px solid #f0ebe4' }}>
              <span style={{ fontSize: '12px', color: '#888' }}>
                {lines.filter(l => l.type !== 'note' && l.price).length} line item{lines.filter(l=>l.type!=='note'&&l.price).length!==1?'s':''}
              </span>
              <span style={{ fontSize: '13px', fontWeight: '500', color: '#1a1a1a', fontVariantNumeric: 'tabular-nums' }}>${subtotal.toFixed(2)}</span>
            </div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#999', marginBottom: '8px',
              textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tax</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '13px' }}>
              {taxOptions.map(opt => {
                const active = opt.value === 'none' ? !taxEnabled : taxEnabled && taxRate === opt.rate;
                return (
                  <button key={opt.value} onClick={() => { setTaxEnabled(opt.value !== 'none'); setTaxRate(opt.rate); }}
                    style={{ padding: '8px 12px', borderRadius: '7px', fontSize: '12px',
                      fontFamily: 'inherit', fontWeight: active ? '600' : '500',
                      border: `0.5px solid ${active ? WALNUT : '#e0d5cc'}`,
                      background: active ? WALNUT : 'white', color: active ? 'white' : '#555',
                      cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between',
                      transition: 'all 0.15s' }}>
                    <span>{opt.label}</span>
                    {active && taxEnabled && opt.value !== 'none' && (
                      <span style={{ fontWeight: '500', opacity: 0.9, fontVariantNumeric: 'tabular-nums' }}>+${taxAmount.toFixed(2)}</span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Grand total */}
            <div style={{ borderTop: '0.5px solid #e8e2da', paddingTop: '14px' }}>
              <div style={{ fontSize: '10px', color: '#999', marginBottom: '4px', fontWeight: '600', letterSpacing: '0.6px' }}>GRAND TOTAL</div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: WALNUT, letterSpacing: '-0.6px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                ${grandTotal.toFixed(2)}
              </div>
            </div>

            {/* Approve CTA — the natural "close this out" moment, right under the total */}
            <div style={{ borderTop: '0.5px dashed #e8e2da', marginTop: '16px', paddingTop: '16px' }}>
              {estimateStatus === 'approved' ? (
                <button onClick={() => navigate(`/estimates/job-order/${estimateDocId}`)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '13px', borderRadius: '10px', border: `1.5px solid ${MOSS}`,
                    background: '#EAF1EA', color: MOSS, fontSize: '14px', fontWeight: '700',
                    cursor: 'pointer', fontFamily: FONT_SANS }}>
                  <ClipboardCheck size={16} strokeWidth={2} /> Open Job Order
                </button>
              ) : (
                <>
                  <button onClick={handleApproveAndCreateJobOrder} disabled={saving}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      padding: '14px', borderRadius: '10px', border: 'none',
                      background: MOSS, color: 'white', fontSize: '15px', fontWeight: '700',
                      cursor: saving ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS,
                      boxShadow: '0 3px 12px rgba(59,107,68,0.28)', letterSpacing: '-0.1px',
                      opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s' }}>
                    <CheckCircle2 size={17} strokeWidth={2} /> Approve & Create Job Order
                  </button>
                  <div style={{ fontSize: '10.5px', color: STONE, textAlign: 'center', marginTop: '7px', lineHeight: 1.4 }}>
                    Marks this estimate Approved and takes you straight to Job Order setup
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Notes card */}
          <div style={{ background: 'white', borderRadius: '10px', padding: '18px 20px',
            border: '0.5px solid #e8e2da' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '10px',
              textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notes / Terms</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Prices subject to change without notice. Valid for 30 days."
              rows={5}
              style={{ width: '100%', padding: '9px 10px', border: '1px solid #e8e0d8',
                borderRadius: '9px', fontSize: '12px', fontFamily: 'inherit',
                resize: 'vertical', boxSizing: 'border-box', outline: 'none',
                lineHeight: 1.6, color: '#5c4a38' }} />
          </div>

          {/* PDF button */}
          <button onClick={async () => {
            const blob = await generateQuotePDFBlob(
              { customer, orderDate, notes, taxEnabled, taxRate, lines, subtotal, taxAmount, grandTotal, estimateId: estimateDocId || 'DRAFT' },
              userProfile || {}
            );
            setPreviewBlob(blob);
          }} style={{ width: '100%', padding: '13px',
            background: WALNUT,
            border: 'none', borderRadius: '10px', color: 'white', fontSize: '14px',
            fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
            letterSpacing: '-0.1px' }}>
            Preview / Download PDF
          </button>
        </div>
      </div>

      {/* Floating TWP Calculator */}
      <TWPPriceCalculator
        lines={lines}
        onUsePrice={handleUsePrice}
        floating={true}
        editConfig={editConfig}
        onClearEdit={() => setEditConfig(null)}
      />

      {/* Send modal */}
      {showSendModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,
          display:'flex',alignItems:'center',justifyContent:'center',padding:'20px' }}>
          <div style={{ background:PAPER,borderRadius:'16px',padding:'26px',maxWidth:'400px',
            width:'100%',boxShadow:'0 24px 64px rgba(0,0,0,0.3)',fontFamily:FONT_SANS }}>
            {sending && (
              <div style={{ textAlign:'center',padding:'20px 0' }}>
                <div style={{ width:'46px',height:'46px',border:`4px solid #f0e8e0`,
                  borderTop:`4px solid ${WALNUT}`,borderRadius:'50%',margin:'0 auto 16px'}}/>
                <div style={{ fontSize:'16px',fontWeight:'700',marginBottom:'5px',color:INK }}>Sending…</div>
                <div style={{ fontSize:'13px',color:STONE }}>Email & SMS to {customer.name||'customer'}</div>
              </div>
            )}
            {!sending && sentSuccess && (
              <div style={{ textAlign:'center',padding:'10px 0' }}>
                <CheckCircle2 size={44} strokeWidth={1.5} color={MOSS} style={{ marginBottom:'12px' }} />
                <div style={{ fontFamily:FONT_DISPLAY,fontSize:'20px',fontWeight:600,color:MOSS,marginBottom:'8px' }}>Sent!</div>
                <div style={{ fontSize:'13px',color:STONE,marginBottom:'20px' }}>
                  {customer.name} received a link to view and approve.
                </div>
                <button onClick={()=>{setShowSendModal(false);setSentSuccess(false);}}
                  style={{ padding:'11px 28px',borderRadius:'9px',border:'none',
                    background:WALNUT,color:'white',
                    fontSize:'14px',fontWeight:'700',cursor:'pointer',fontFamily:FONT_SANS }}>Done</button>
              </div>
            )}
            {!sending && !sentSuccess && (
              <>
                <h2 style={{ display:'flex',alignItems:'center',gap:'8px',margin:'0 0 6px',fontFamily:FONT_DISPLAY,fontSize:'19px',fontWeight:600,color:INK }}>
                  <Send size={17} strokeWidth={2} color={WALNUT} /> Send to Customer
                </h2>
                <p style={{ margin:'0 0 16px',fontSize:'13px',color:STONE }}>
                  They&rsquo;ll get a link to view and approve their estimate online.
                </p>
                <div style={{ background:'#f9f6f2',borderRadius:'9px',padding:'12px',marginBottom:'16px' }}>
                  <div style={{ fontSize:'15px',fontWeight:'700',color:INK }}>{customer.name||'—'}</div>
                  {customer.email&&<div style={{ display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',color:STONE,marginTop:'4px' }}><Mail size={12} strokeWidth={2} /> {customer.email}</div>}
                  {(customer.phone||customer.cellPhone)&&<div style={{ display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',color:STONE,marginTop:'4px' }}><Smartphone size={12} strokeWidth={2} /> {customer.phone||customer.cellPhone}</div>}
                  {!customer.email&&!customer.phone&&!customer.cellPhone&&
                    <div style={{ display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',color:RUST,fontWeight:'600',marginTop:'4px' }}><AlertTriangle size={12} strokeWidth={2} /> No email or phone on file.</div>}
                </div>
                <div style={{ display:'flex',gap:'9px' }}>
                  <button onClick={()=>setShowSendModal(false)}
                    style={{ flex:1,padding:'11px',borderRadius:'9px',border:'1px solid #e5e5e5',
                      background:'white',color:STONE,fontSize:'13px',cursor:'pointer',fontWeight:'600',fontFamily:FONT_SANS }}>Cancel</button>
                  <button onClick={handleSendToCustomer}
                    disabled={!customer.email&&!customer.phone&&!customer.cellPhone}
                    style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',flex:2,padding:'11px',borderRadius:'9px',border:'none',
                      background:WALNUT,color:'white',
                      fontSize:'13px',cursor:'pointer',fontWeight:'700',fontFamily:FONT_SANS,
                      boxShadow:'0 3px 10px rgba(139,69,19,0.28)' }}>
                    <CheckCircle2 size={14} strokeWidth={2} /> Confirm &amp; Send
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {previewBlob && (
        <PDFPreviewModal pdfBlob={previewBlob}
          filename={`${(customer?.name||'Estimate').replace(/\s+/g,'_')}_Quote.pdf`}
          onClose={() => setPreviewBlob(null)} />
      )}
    </div>
    </AppLayout>
  );
}